import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  Renderer2,
} from '@angular/core';

/**
 * appReveal — hiệu ứng "xuất hiện lần lượt" kiểu landing page.
 *
 * Phần tử bắt đầu ở trạng thái mờ + dịch xuống nhẹ, khi cuộn vào viewport
 * sẽ trượt lên và hiện ra. Dùng độ trễ (ms) để tạo hiệu ứng so le giữa các phần.
 *
 * Ví dụ:
 *   <div appReveal></div>
 *   <div appReveal="120"></div>   <!-- trễ 120ms -->
 *
 * - Chạy một lần (one-shot), không lặp lại khi cuộn qua lại.
 * - Tôn trọng prefers-reduced-motion: hiện ngay, không animation.
 */
@Directive({
  selector: '[appReveal]',
})
export class RevealOnScrollDirective implements AfterViewInit, OnDestroy {
  /** Độ trễ (ms) trước khi phần tử hiện — dùng để tạo hiệu ứng so le. */
  @Input('appReveal') delay: string | number = 0;

  private observer?: IntersectionObserver;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private zone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    const node = this.el.nativeElement;

    // Người dùng tắt animation hệ thống → hiện ngay, không xử lý thêm.
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const delayMs = Number(this.delay) || 0;

    // Trạng thái ẩn ban đầu.
    this.renderer.setStyle(node, 'opacity', '0');
    this.renderer.setStyle(node, 'transform', 'translateY(24px)');
    this.renderer.setStyle(node, 'will-change', 'opacity, transform');
    this.renderer.setStyle(
      node,
      'transition',
      `opacity .6s cubic-bezier(.22,.61,.36,1) ${delayMs}ms, transform .6s cubic-bezier(.22,.61,.36,1) ${delayMs}ms`,
    );

    this.zone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.reveal();
              this.observer?.disconnect();
            }
          });
        },
        { threshold: 0.15 },
      );
      this.observer.observe(node);
    });
  }

  private reveal(): void {
    const node = this.el.nativeElement;
    this.renderer.setStyle(node, 'opacity', '1');
    this.renderer.setStyle(node, 'transform', 'none');

    // Sau khi hiện xong, gỡ inline style để không đè lên hover/transition của SCSS
    // (vd: .tool-card:hover translateY). Chỉ chạy một lần.
    const cleanup = () => {
      this.renderer.removeStyle(node, 'opacity');
      this.renderer.removeStyle(node, 'transform');
      this.renderer.removeStyle(node, 'transition');
      this.renderer.removeStyle(node, 'will-change');
      node.removeEventListener('transitionend', cleanup);
    };
    node.addEventListener('transitionend', cleanup);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
